import React, { useEffect, useMemo, useState } from "react";
import Footer from "../common/Footer";
/**
 * Open Source Licenses – React Component (List Only)
 * -------------------------------------------------
 * How to use:
 * - Provide `data` prop (preferred) or host `/licenses.json` and use `sourceUrl`.
 * - No filters, search, or print buttons — just a clean expandable list.
 *
 * Props:
 * - title?: string (default: "Open Source Licenses")
 * - data?: LicensesData
 * - sourceUrl?: string (default: "/licenses.json")
 * - className?: string
 *
 * LicensesData schema:
 * {
 *   generatedAt: string; // YYYY-MM-DD (or ISO)
 *   packages: Array<{
 *     name: string;
 *     version?: string;
 *     license?: string;        // SPDX id or expression
 *     homepage?: string;
 *     authors?: string[];
 *     copyright?: string;
 *     licenseText: string;     // FULL license text (required for compliance)
 *     noticeText?: string;     // Apache NOTICE etc.
 *   }>;
 * }
 */

export default function OpenSource({
  title = "Open Source Licenses",
  data,
  sourceUrl = "/resources/app/licenses.json",
  className,
}) {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState("");
  

  useEffect(() => {
    let ignore = false;
    async function run() {
      if (data) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await fetch(sourceUrl, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!ignore) setFetched(json);
      } catch (e) {
        if (!ignore) setError(String(e?.message || e));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [data, sourceUrl]);

  const payload = data || fetched || sampleData; // fallback sample for dev

  const packages = useMemo(() => {
    const arr = Array.isArray(payload?.packages) ? payload.packages.slice() : [];
    arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    return arr;
  }, [payload]);

  const generatedAt = payload?.generatedAt || new Date().toISOString().slice(0, 10);

  return (
    <>
    <div id="container" className={"osl-wrap " + (className || "")} role="region" aria-label="Open Source Licenses">
      <Style/>

      <header className="osl-header">
        <div>
          <h1 className="osl-title">{title}</h1>
          <div className="osl-sub">This application includes third‑party open‑source software. Notices are provided below.</div>
          <div className="osl-sub small">Last updated — {generatedAt}</div>
        </div>
        {loading && <span className="osl-badge" aria-live="polite">Loading…</span>}
        {error && <span className="osl-badge bad">Error: {error}</span>}
      </header>

      <div id="osl-list" aria-live="polite" aria-busy={loading || undefined}>
        {packages.map((p, idx) => (
          <DetailsCard key={p.name + idx} pkg={p} />
        ))}
        {!packages.length && !loading && (
          <div className="osl-empty">No packages found.</div>
        )}
      </div>

      <footer className="osl-footer">
        <div>
          For Apache‑2.0 components, any <em>NOTICE</em> file text is included below as required by the license.
        </div>
        <div>
          If you are the copyright holder of a component and believe attribution is missing or incorrect, please contact us.
        </div>
      </footer>
    </div>
    
    <Footer lang={"en"} className="mt0"></Footer>
    </>
  );
}

function DetailsCard({ pkg }) {
  return (
    <details className="osl-card">
      <summary>
        <div className="osl-pkg">
          <span className="osl-name">{pkg.name || "Unknown"}</span>
          {pkg.version ? <span className="osl-ver">@ {pkg.version}</span> : null}
        </div>
        <span className="osl-license">{pkg.license || "Unknown"}</span>
      </summary>
      <div className="osl-content">
        <Row label="Homepage">
          {pkg.homepage ? (
            <a className="osl-link" href={pkg.homepage} target="_blank" rel="noopener noreferrer">
              {pkg.homepage}
            </a>
          ) : (
            <span>—</span>
          )}
        </Row>
        <Row label="Authors">
          {pkg.authors?.length ? <span>{pkg.authors.join(", ")}</span> : <span>—</span>}
        </Row>
        <Row label="Copyright">
          <span>{pkg.copyright || "—"}</span>
        </Row>
        {pkg.noticeText ? (
          <Row label="NOTICE">
            <pre className="osl-mono">{pkg.noticeText}</pre>
          </Row>
        ) : null}
        <Row label="License Text">
          <pre className="osl-mono">{pkg.licenseText || "(license text not provided)"}</pre>
        </Row>
      </div>
    </details>
  );
}

function Row({ label, children }) {
  return (
    <div className="osl-row">
      <div className="osl-label">{label}</div>
      <div>{children}</div>
    </div>
    
  );
}

function Style() {
    return (
    <style>{`
    :root {
    --bg: #ffffff;
    --card: #ffffff;
    --muted: #475467; /* gray-600 */
    --text: #111827; /* gray-900 */
    --accent: #2563eb; /* blue-600 */
    --border: #e5e7eb; /* gray-200 */
    --radius: 12px;
    --shadow: 0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.10);
    }
    
    
    .osl-wrap { color: var(--text); background: var(--bg); padding: 32px 20px 80px; }
    .osl-header { max-width: 1080px; margin: 0 auto 18px; display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
    .osl-title { font-size: clamp(22px, 3.6vw, 34px); margin: 0; letter-spacing: 0.2px; color: var(--text); }
    .osl-sub { color: var(--muted); font-size: 13px; }
    .osl-sub.small { margin-top: 4px; font-size: 12px; }
    .osl-right { display:flex; gap:8px; }
    
    
    .osl-badge { background: #f2f4f7; color: #344054; border: 1px solid var(--border); padding: 6px 10px; border-radius: 999px; font-size: 12px; height: fit-content; }
    .osl-badge.bad { border-color:#fda29b; background:#fee4e2; color:#b42318; }
    
    
    .osl-card { max-width: 1080px; margin: 10px auto 0; background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); }
    .osl-card > summary { list-style: none; cursor: pointer; padding: 14px 16px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
    .osl-card > summary::-webkit-details-marker { display:none; }
    .osl-pkg { display:flex; align-items:center; gap: 12px; min-width:0; }
    .osl-name { font-weight: 600; color: var(--text); }
    .osl-ver { color: #667085; font-weight: 500; }
    .osl-license { font-size: 12px; padding: 4px 8px; border-radius: 999px; border:1px solid var(--border); color: var(--accent); background:#eef2ff; }
    
    
    .osl-content { padding: 0 16px 16px; }
    .osl-row { display:grid; grid-template-columns: 120px 1fr; gap: 12px; margin: 10px 0; align-items: start; }
    .osl-label { color: #667085; font-size: 12px; padding-top: 4px; }
    .osl-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; font-size: 12px; background:#f9fafb; border:1px solid var(--border); border-radius: 10px; padding: 12px; overflow:auto; max-height: 50vh; white-space: pre-wrap; color: var(--text); }
    .osl-link { color: var(--accent); text-decoration: none; }
    .osl-link:hover { text-decoration: underline; }
    
    
    .osl-footer { max-width: 1080px; margin: 26px auto 0; color: #667085; font-size: 12px; display:flex; justify-content:space-between; gap: 12px; flex-wrap: wrap; }
    
    
    @media (max-width: 640px) { .osl-row { grid-template-columns: 1fr; } .osl-label { padding-top: 0; } }
    @media print { .osl-wrap { background: white; color: #111; } .osl-card { break-inside: avoid; border-color: #d0d5dd; box-shadow: none; } .osl-mono { max-height: initial; } }
    `}</style>
    );
    }

// --- Fallback sample data (remove in production) ---
const sampleData = {
  generatedAt: new Date().toISOString().slice(0, 10),
  packages: [
    {
      name: "Lodash",
      version: "4.17.21",
      license: "MIT",
      homepage: "https://lodash.com",
      authors: ["JS Foundation and other contributors"],
      copyright: "© JS Foundation and other contributors",
      licenseText:
        "Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the 'Software'), to deal in the Software without restriction…",
    },
    {
      name: "OkHttp",
      version: "4.12.0",
      license: "Apache-2.0",
      homepage: "https://square.github.io/okhttp/",
      authors: ["Square, Inc."],
      copyright: "© Square, Inc.",
      noticeText: "This product includes software developed by Square, Inc.",
      licenseText:
        "Apache License Version 2.0, January 2004 http://www.apache.org/licenses/…",
    },
    {
      name: "Zlib",
      version: "1.2.13",
      license: "Zlib",
      homepage: "https://zlib.net/",
      authors: ["Jean-loup Gailly", "Mark Adler"],
      copyright: "© 1995–2023 Jean-loup Gailly and Mark Adler",
      licenseText: "This software is provided 'as-is', without any express or implied warranty…",
    },
  ],
};



// export default function OpenSource(prop) {
//     let lang = prop.lang ? prop.lang : "kr";
//     useEffect(() => {
//         window.scrollTo(0, 0);

//     }, []);
    
//     return ( 
//         <>
//             <div id="container" >
//                 <div id="contents" className="contact">
//                     <div className="page_title-section">
//                         <div className="section-inner">
//                             <div className="center-box">
//                                 <p className="page_title">
//                                     <span>Open Source Licenses</span>
//                                 </p>
//                             </div>
//                         </div>
//                     </div>
//                     <section className="section full-section privacy_wrap">
//                         <div className="section-inner">
//                             <div className="center-box">
//                                 <div className="content-panel">
//                                     <div className="panel-header ">
                                    
//                                         <p className="tit point_circle white_circle">
//                                         Open Source Licenses
//                                         </p>
//                                     </div>
//                                     <div className="panel-body">
                                        
//                                         <div className="privacy-contents">
//                                             <p>
//                                                 11
//                                             </p>
                                            
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </section>
//                 </div>
//             </div>
//             <Footer lang={lang} className="mt0"></Footer>
//         </>
//     )
// }